import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { StateStoreService } from '../services/state-store/state-store';

/**
 * Billing route gate — `billing_read` on the caller's **home** org.
 *
 * The sidebar entry (`showBillingMenuItem`) uses the same `holdsHomeCapability('billing_read', …)`,
 * so the menu and the route agree: a member holding `billing_read` without an ADMIN row sees the
 * entry and reaches the page.
 *
 * `isAdmin()` is the **legacy answer**, not a second condition. `holdsHomeCapability` returns it
 * verbatim for any org the backend composed no access block for, so an unconfigured org is gated
 * by role as before. Absent access data is permissive, never "no capabilities" — see `OrgAccess`
 * in `models/access-control.model.ts`.
 *
 * Home rather than effective, matching what the page acts on: Billing reads
 * `selectedOrgId()`, so picking a customer does not change whose
 * subscription it manages.
 *
 * Deliberately **not** mirroring the menu's `isSuperManager() !== true` half.
 * That hides a dead-end entry for orgs entitled by `org_type`; it is not an
 * authorisation statement, and enforcing it here would newly bounce a
 * SUPER_MANAGER admin who deep-links.
 *
 * `billing_read` is the page gate. The write actions on it (change plan,
 * cancel, manage payment method) are a separate question — `billing_write`
 * — and are not gated at the time of writing.
 *
 * Readiness is guaranteed by `appReadyMatch` at the route level:
 * `_currentMemberRole` is populated before ready flips, which is what stops
 * a refresh on this route bouncing while the role fetch is still in flight.
 */
export const billingGuard: CanActivateFn = () => {
  const state = inject(StateStoreService);
  const router = inject(Router);

  return state.holdsHomeCapability('billing_read', state.isAdmin())
    ? true
    : router.createUrlTree(['/assessment']);
};
